import { CreateAccountModalComponent } from '@account-users/account/components/create-account-modal/create-account-modal.component';
import { LicensePackage } from '@account-users/account/models';
import {
  AccountCategory,
  AccountCategoryCode,
} from '@account-users/account/models/AccountCategory';
import {
  FROM_DOMAINS_FILTER_KEYS,
  FromDomainsFilterChange,
  FromDomainsFilterKey,
  FromDomainsFilterState,
  hasPrivateDomainFilter,
  parseFromDomainsFilterParam,
} from '@account-users/account/models/FromDomainsFilter';
import { AccountState } from '@account-users/account/models/IAccount';
import { Permission } from '@account-users/account/models/Permission';
import * as fromAccount from '@account-users/account/state';
import {
  LoadAccountCategories,
  LoadAccounts,
  ResetCreateAccountState,
  SortAccountsAction,
} from '@account-users/account/state/accounts/accounts.actions';
import { State } from '@account-users/account/state/accounts/accounts.reducer';
import { FetchLicensePackage } from '@account-users/account/state/licenses/licenses.actions';
import { AuthService } from '@account-users/auth/auth.service';
import { UserRolesAndPermissionsService } from '@account-users/user/services/user-roles-and-permissions.service';
import * as fromPermissions from '@account-users/user/state';
import { LoadPermissions } from '@account-users/user/state/permissions/permissions.actions';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store, select } from '@ngrx/store';
import { removeItemFromArray } from '@shared';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';

@Component({
  selector: 'app-account-list',
  templateUrl: './account-list.component.html',
  styleUrls: ['./account-list.component.scss'],
})
export class AccountListComponent implements OnInit, OnDestroy {
  public createAccountModalRef: NgbModalRef;
  public AccountState = AccountState;
  public userPermissions: Permission[] = [];
  public categories: string[] = [];
  public currentSearchQuery = '';
  public currentSearchCategories: string[] = [];
  public categoryString = '';
  public query = '';
  public loadAllAccounts = false;
  public categoriesArrayFilters$: Subject<AccountCategory[]>;

  public fromDomainsFilterState: FromDomainsFilterState = {};

  public licensePackages$: Observable<LicensePackage[]>;
  public accountCategories$: Observable<AccountCategory[]>;
  public hasOnlyTrialDispenserRole$: Observable<boolean>;
  public userPermissions$ = this.store.select(
    fromPermissions.getUserPermissions,
  );
  public accounts$ = this.store.select(fromAccount.getFilteredAccounts);
  public sortOnAscending$ = this.store.select(fromAccount.getAccountSortOn);
  public isLoadingAccounts$ = this.store.select(
    fromAccount.getIsLoadingAccounts,
  );

  private customCategoriesObj: Record<string, boolean> = {};
  private debounceIndex = 0;
  private filterDebouncer$: Subject<any> = new Subject<any>();
  private destroy$: Subject<void> = new Subject<void>();
  private readonly subscriptions = new Subscription();
  private readonly customCategories: AccountCategory[];

  constructor(
    private readonly modalService: NgbModal,
    private readonly auth: AuthService,
    private readonly toastr: ToastrService,
    private readonly store: Store<State>,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userRolesService: UserRolesAndPermissionsService,
  ) {
    this.customCategories = [
      {
        id: 1000,
        displayName: 'DELETED ONLY',
        code: AccountCategoryCode.DeletedOnly,
      },
    ];
  }

  ngOnInit() {
    if (!this.auth.isAuthenticated()) {
      return;
    }

    this.hasOnlyTrialDispenserRole$ = this.userRolesService.hasOnlyTrialDispenserRole$;

    this.licensePackages$ = this.store.pipe(
      select(fromAccount.getLicensePackages),
    );

    this.accountCategories$ = this.store.pipe(
      select(fromAccount.getAccountCategories),
      map((accountCategories) => [
        ...accountCategories,
        ...this.customCategories,
      ]),
    );

    this.subscriptions.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        this.query = params['q'];
        this.categoryString = params['c'];
        this.customCategoriesObj['deleted'] = params['deleted'];
        this.fromDomainsFilterState = this.readFromDomainsFiltersFromParams(
          params,
        );
      }),
    );

    this.setupFilterDebouncer();
    this.loadAccounts();

    this.store.dispatch(new FetchLicensePackage());
    this.store.dispatch(new LoadAccountCategories());

    this.store.dispatch(new LoadPermissions());
    this.userPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => (this.userPermissions = p));
  }

  ngOnDestroy() {
    this.filterDebouncer$.unsubscribe();
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.toastr.clear();
  }

  loadAccounts() {
    this.store.dispatch(
      new LoadAccounts({
        query: this.getQuery(),
        categories: this.categoryArray(),
        // PD-based filters (from-domains-exist / from-domains-confirmed) are applied as a
        // post-filter in BO Gateway after the paginated SQL fetch. Forcing loadAll avoids
        // empty/incomplete pages when most of the 50-row page is dropped by the post-filter.
        loadAll: this.loadAllAccounts || this.hasActivePrivateDomainFilter(),
        filters: {
          ...this.getCustomFilters(),
          ...this.getFromDomainsHttpFilters(),
        },
      }),
    );
  }

  hasActivePrivateDomainFilter(): boolean {
    return hasPrivateDomainFilter(this.fromDomainsFilterState);
  }

  onFromDomainsFilterChange({ key, value }: FromDomainsFilterChange) {
    const current = this.fromDomainsFilterState[key];
    const next = current === value ? undefined : value;
    this.fromDomainsFilterState = this.applyFromDomainsFilter(
      this.fromDomainsFilterState,
      key,
      next,
    );
    this.setParams({ [key]: next === undefined ? null : String(next) });
    this.debounceIndex++;
    this.filterDebouncer$.next(`${key}=${next}-${this.debounceIndex}`);
  }

  getPathFilters() {
    const categoryPathFilters = this.categoryArray();
    const customAccountFilters = Object.entries(this.customCategoriesObj)
      .filter(([_, value]) => !!value)
      .map(([key, _]) => key);
    return [...categoryPathFilters, ...customAccountFilters];
  }

  categoryArray() {
    return this.categoryString && this.categoryString.length
      ? this.categoryString.split('+')
      : [];
  }

  openCreateAccountModal() {
    this.store.dispatch(new ResetCreateAccountState());

    this.createAccountModalRef = this.modalService.open(
      CreateAccountModalComponent,
      { size: 'lg', backdrop: 'static', keyboard: false },
    );
  }

  get canCreateAccount() {
    return this.userPermissions.includes(Permission.CanCreateAccount);
  }

  setParams(params) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  setSearchQuery(query) {
    this.setParams({ q: query });
    this.debounceIndex++;
    this.filterDebouncer$.next(query + this.debounceIndex);
  }

  filterByCategory(payload: { category: string; toggle: boolean }) {
    const { category, toggle } = payload;
    const isCustomCategory = this.customCategories.some(
      (c) => c.code === category,
    );
    if (isCustomCategory) {
      if (!toggle) {
        this.customCategoriesObj[`${category}`] = undefined;
      } else {
        this.customCategoriesObj = {
          ...this.customCategoriesObj,
          [`${category}`]: toggle,
        };
      }

      this.setParams({ ...this.customCategoriesObj });

      this.debounceIndex++;
      this.filterDebouncer$.next(category + this.debounceIndex);
    } else {
      const categories = this.setCategoryArray(category);
      const cat = categories.join('+');
      this.setParams({ c: cat });
      this.debounceIndex++;
      this.filterDebouncer$.next(cat + this.debounceIndex);
    }
  }

  setCategoryArray(category) {
    const index = [...this.categoryArray()].indexOf(category);
    return index === -1
      ? [...this.categoryArray(), category]
      : removeItemFromArray([...this.categoryArray()], category);
  }

  setupFilterDebouncer(): void {
    this.filterDebouncer$
      .pipe(debounceTime(600), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.loadAccounts());
  }

  sortAccounts() {
    this.store.dispatch(new SortAccountsAction());
  }

  loadAllHit() {
    this.loadAllAccounts = true;
    this.store.dispatch(
      new LoadAccounts({
        query: this.getQuery(),
        categories: this.categoryArray(),
        loadAll: true,
        filters: {
          ...this.getCustomFilters(),
          ...this.getFromDomainsHttpFilters(),
        },
      }),
    );
  }

  private getQuery() {
    return this.query ? this.query : '';
  }

  private getCustomFilters() {
    return Object.entries(this.customCategoriesObj)
      .filter(([_, value]) => !!value)
      .reduce((a, [key, value]) => {
        a[key] = value;
        return a;
      }, {});
  }

  private getFromDomainsHttpFilters(): Record<string, string> {
    return FROM_DOMAINS_FILTER_KEYS.reduce<Record<string, string>>(
      (acc, key) => {
        const value = this.fromDomainsFilterState[key];
        if (typeof value === 'boolean') {
          acc[key] = String(value);
        }
        return acc;
      },
      {},
    );
  }

  private readFromDomainsFiltersFromParams(
    params: Record<string, unknown>,
  ): FromDomainsFilterState {
    return FROM_DOMAINS_FILTER_KEYS.reduce<FromDomainsFilterState>(
      (acc, key) => {
        const value = parseFromDomainsFilterParam(params[key]);
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
  }

  private applyFromDomainsFilter(
    state: FromDomainsFilterState,
    key: FromDomainsFilterKey,
    value: boolean | undefined,
  ): FromDomainsFilterState {
    const next: FromDomainsFilterState = { ...state };
    if (value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    return next;
  }
}
